import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Plus, X, Check, Copy, CheckCheck, Ban, AlertTriangle } from 'lucide-react';

import { api } from '../../api/client.js';
import './ApiTokensPanel.css';

/**
 * API Tokens — personal access tokens for programmatic clients
 * (the MCP server, n8n, scripts).
 *
 * Every role can manage their own tokens: a token never grants more than the
 * user it belongs to already has. The raw token is returned by the API exactly
 * once, on creation, so the result modal is the user's only chance to copy it —
 * same contract, and same styling, as the password reset modal.
 */

function formatDate(iso) {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatLastUsed(iso) {
    if (!iso) return 'Never used';
    const then = new Date(iso);
    const mins = Math.floor((Date.now() - then.getTime()) / 60000);
    if (mins < 1)    return 'Just now';
    if (mins < 60)   return `${mins} min ago`;
    if (mins < 1440) return `${Math.floor(mins / 60)} h ago`;
    return formatDate(iso);
}

export function ApiTokensPanel() {
    const queryClient = useQueryClient();

    const [naming, setNaming]           = useState(false);   // create modal open
    const [newName, setNewName]         = useState('');
    const [created, setCreated]         = useState(null);    // { name, token }
    const [copied, setCopied]           = useState(false);
    const [revokeTarget, setRevokeTarget] = useState(null);

    const { data: tokens, isLoading, error } = useQuery({
        queryKey: ['api-tokens'],
        queryFn: () => api.get('/tokens'),
    });

    const createMutation = useMutation({
        mutationFn: (name) => api.post('/tokens', { name }),
        onSuccess: (data) => {
            setCreated({ name: data.name, token: data.token });
            setNaming(false);
            setNewName('');
            queryClient.invalidateQueries({ queryKey: ['api-tokens'] });
        },
    });

    const revokeMutation = useMutation({
        mutationFn: (id) => api.delete(`/tokens/${id}`),
        onSuccess: () => {
            setRevokeTarget(null);
            queryClient.invalidateQueries({ queryKey: ['api-tokens'] });
        },
    });

    const handleCopy = () => {
        if (!created) return;
        navigator.clipboard.writeText(created.token).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const handleDismissCreated = () => {
        setCreated(null);
        setCopied(false);
    };

    const handleOpenCreate = () => {
        createMutation.reset();
        setNewName('');
        setNaming(true);
    };

    if (isLoading) return <div className="panel-empty"><span className="spinner" /></div>;
    if (error) return <div className="panel-error">Couldn't load API tokens: {error.message}</div>;

    const list = Array.isArray(tokens) ? tokens : [];

    return (
        <div className="tokens-panel">
            <div className="tokens-intro">
                <p className="tokens-intro-text">
                    API tokens let tools like Claude Code or n8n act on the CRM on your behalf.
                    A token can do exactly what you can do, no more. Treat one like a password.
                </p>
                <button className="btn-primary-small" onClick={handleOpenCreate}>
                    <Plus size={14} strokeWidth={2} /> Generate new token
                </button>
            </div>

            {list.length === 0 ? (
                <div className="tokens-empty">
                    <KeyRound size={22} strokeWidth={1.5} />
                    <p>No API tokens yet.</p>
                    <span>Generate one to connect Claude Code or another tool to the CRM.</span>
                </div>
            ) : (
                <ul className="token-list">
                    {list.map(t => (
                        <li key={t.id} className={`token-row${t.is_revoked ? ' token-row-revoked' : ''}`}>
                            <div className="token-icon">
                                <KeyRound size={15} strokeWidth={1.75} />
                            </div>

                            <div className="token-info">
                                <div className="token-name">
                                    {t.name}
                                    {t.is_revoked && <span className="token-revoked-pill">Revoked</span>}
                                </div>
                                <div className="token-meta">
                                    <code className="token-prefix">{t.token_prefix}&hellip;</code>
                                    <span className="token-dot">&middot;</span>
                                    <span>{t.is_revoked
                                        ? `Revoked ${formatDate(t.revoked_at)}`
                                        : formatLastUsed(t.last_used_at)}</span>
                                    <span className="token-dot">&middot;</span>
                                    <span>Created {formatDate(t.created_at)}</span>
                                </div>
                            </div>

                            {!t.is_revoked && (
                                <div className="token-actions">
                                    <button
                                        className="token-action-btn token-action-danger"
                                        onClick={() => { revokeMutation.reset(); setRevokeTarget(t); }}
                                        title="Revoke this token"
                                    >
                                        <Ban size={14} strokeWidth={1.75} /> Revoke
                                    </button>
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            )}

            {/* ---------------------------------------------------------------
              * Create — name the token
              * ------------------------------------------------------------- */}
            {naming && (
                <div className="modal-overlay" onClick={() => setNaming(false)}>
                    <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
                        <header className="modal-header">
                            <h2 className="modal-title">Generate API token</h2>
                            <button className="modal-close" onClick={() => setNaming(false)} aria-label="Close">
                                <X size={16} strokeWidth={2} />
                            </button>
                        </header>
                        <div className="modal-body">
                            <form onSubmit={(e) => {
                                e.preventDefault();
                                const name = newName.trim();
                                if (name) createMutation.mutate(name);
                            }}>
                                <label className="token-label" htmlFor="token-name">
                                    What is this token for?
                                </label>
                                <input
                                    id="token-name"
                                    className="detail-field-input"
                                    value={newName}
                                    onChange={(e) => setNewName(e.target.value)}
                                    placeholder="Claude Code"
                                    maxLength={60}
                                    autoFocus
                                />
                                <p className="token-hint">
                                    A label just for you, so you can tell tokens apart later
                                    &mdash; for example &ldquo;Claude Code&rdquo; or &ldquo;n8n&rdquo;.
                                </p>

                                {createMutation.isError && (
                                    <div className="panel-error">{createMutation.error.message}</div>
                                )}

                                <div className="modal-actions">
                                    <button type="button" className="btn-ghost"
                                            onClick={() => setNaming(false)}
                                            disabled={createMutation.isPending}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn-primary-small"
                                            disabled={createMutation.isPending || !newName.trim()}>
                                        {createMutation.isPending ? 'Generating…' : 'Generate token'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* ---------------------------------------------------------------
              * Created — show the raw token exactly once
              * ------------------------------------------------------------- */}
            {created && (
                <div className="modal-overlay">
                    <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
                        <header className="modal-header">
                            <h2 className="modal-title">API token generated</h2>
                        </header>
                        <div className="modal-body">
                            <p>Token for <strong>{created.name}</strong>:</p>

                            <div className="token-display">
                                <code className="token-code">{created.token}</code>
                            </div>

                            <button className="btn-ghost token-copy-btn" onClick={handleCopy}>
                                {copied
                                    ? <><CheckCheck size={14} strokeWidth={2} /> Copied!</>
                                    : <><Copy size={14} strokeWidth={2} /> Copy to clipboard</>}
                            </button>

                            <div className="token-warning">
                                <AlertTriangle size={15} strokeWidth={2} className="token-warning-icon" />
                                <div>
                                    <strong>This token will only be shown once.</strong>{' '}
                                    Copy it now and store it somewhere safe. If you lose it,
                                    revoke it and generate a new one.
                                </div>
                            </div>

                            <div className="modal-actions">
                                <button className="btn-primary-small" onClick={handleDismissCreated}>
                                    <Check size={14} strokeWidth={2} /> Done
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ---------------------------------------------------------------
              * Revoke — confirmation
              * ------------------------------------------------------------- */}
            {revokeTarget && (
                <div className="modal-overlay" onClick={() => setRevokeTarget(null)}>
                    <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
                        <header className="modal-header">
                            <h2 className="modal-title">Revoke token?</h2>
                        </header>
                        <div className="modal-body">
                            <p>
                                Revoke <strong>{revokeTarget.name}</strong>{' '}
                                (<code className="token-prefix">{revokeTarget.token_prefix}&hellip;</code>)?
                            </p>
                            <p className="token-hint">
                                Anything using this token stops working immediately.
                                This cannot be undone &mdash; you would need to generate a new token.
                            </p>

                            {revokeMutation.isError && (
                                <div className="panel-error">{revokeMutation.error.message}</div>
                            )}

                            <div className="modal-actions">
                                <button className="btn-ghost"
                                        onClick={() => setRevokeTarget(null)}
                                        disabled={revokeMutation.isPending}>
                                    Cancel
                                </button>
                                <button className="btn-danger-small"
                                        onClick={() => revokeMutation.mutate(revokeTarget.id)}
                                        disabled={revokeMutation.isPending}>
                                    {revokeMutation.isPending
                                        ? 'Revoking…'
                                        : <><Ban size={14} strokeWidth={2} /> Revoke token</>}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
